/**
 * Tax Savings Calculator for Journey Tab
 *
 * Calculates deterministic tax savings breakdown for NRIs returning to India.
 * All numbers are computed here (not by LLM) for accuracy and consistency.
 *
 * KEY TAX INSIGHT:
 * During RNOR, foreign-sourced income (including capital gains) is NOT taxable in India.
 * This creates opportunities to sell US assets during RNOR and avoid India taxation.
 *
 * Tax Savings Categories:
 * 1. RSU/Stock Liquidation - Sell during RNOR to avoid India LTCG
 * 2. Brokerage Liquidation - Sell during RNOR to avoid India LTCG
 * 3. RNOR Foreign Income - Passive income (dividends/interest) tax-free during RNOR
 * 4. 401(k)/IRA Roth Conversion - Convert during RNOR, only US tax applies
 * 5. ESPP Liquidation - Sell during RNOR for optimal tax treatment
 *
 * Supported scenarios:
 * - USA → India: Full optimization suite
 * - UAE → India: RNOR benefit (simpler, no foreign tax to coordinate)
 */

import type { StateTaxTier, RSUGrant } from '@/types'

// ==========================================
// Types
// ==========================================

export interface TaxSavingsItem {
  id: string
  category: string
  description: string
  amountINR: number
  amountFormatted: string
  timing: 'before_move' | 'during_rnor' | 'post_rnor'
  timingDescription: string
  howToCapture: string[]
  confidence: 'high' | 'medium' | 'estimated'
  requiresCAReview: boolean
  // New: Show calculation breakdown for transparency
  calculation?: {
    label: string
    value: string
  }[]
}

export interface TaxSavingsBreakdown {
  total: number
  totalFormatted: string
  items: TaxSavingsItem[]
  assumptions: string[]
  disclaimer: string
}

export interface TaxSavingsInput {
  // Profile
  residentCountry: 'USA' | 'UAE'
  exchangeRate: number

  // USA-specific
  stateTaxTier?: StateTaxTier
  rsuGrants?: RSUGrant[]
  esppBalance?: number
  balance401k?: number
  balanceTraditionalIRA?: number
  taxableBrokerage?: number
  brokerageCostBasis?: number // If not provided, assume 70% is cost basis (30% gain)
  cryptoBalance?: number // Cryptocurrency holdings

  // UAE-specific (no special inputs needed, just RNOR)

  // RNOR
  rnorEligible: boolean
  rnorYears: number

  // Foreign assets for RNOR calculation
  totalForeignAssets: number

  // User's safe withdrawal rate (from Step 7 Assumptions)
  safeWithdrawalRate?: number
}

// ==========================================
// Constants
// ==========================================

// Tax rates (exported for reference)
export const TAX_RATES = {
  // US rates
  US_FEDERAL_LTCG: 0.15,           // 15% for most filers ($44K-$492K income)
  US_FEDERAL_STCG: 0.24,           // Taxed as ordinary income (avg bracket)
  US_ORDINARY_INCOME_AVG: 0.22,    // Average US ordinary income bracket

  // India rates (post-2024 budget)
  INDIA_EQUITY_STCG: 0.20,         // Short-term equity (<1 year holding)
  INDIA_EQUITY_LTCG: 0.125,        // Long-term equity (>1 year, above exemption)
  INDIA_LTCG_EXEMPTION: 125000,    // ₹1.25 lakh annual exemption (Section 112A)
  INDIA_INCOME_TAX_AVG: 0.30,      // Average slab rate for foreign income (30%)
  INDIA_SURCHARGE_THRESHOLD: 5000000, // Surcharge applies above ₹50L income

  // Crypto rates - India has flat 30% tax on crypto (no exemption, no deductions)
  INDIA_CRYPTO_TAX: 0.30,          // Flat 30% on crypto gains (Section 115BBH)
  INDIA_CRYPTO_TDS: 0.01,          // 1% TDS on crypto transactions
} as const

// Assumption constants
export const ASSUMPTIONS = {
  RSU_GAIN_PERCENTAGE: 0.50,       // 50% of RSU value assumed to be gain
  ESPP_DISCOUNT_RATE: 0.15,        // Standard 15% ESPP discount
  ESPP_GAIN_PERCENTAGE: 0.30,      // Additional 30% appreciation assumed
  BROKERAGE_GAIN_PERCENTAGE: 0.30, // 30% of brokerage assumed to be gains
  CRYPTO_GAIN_PERCENTAGE: 0.50,    // 50% of crypto value assumed to be gains (volatile asset)
  FOREIGN_INCOME_SWR: 0.035,       // 3.5% SWR for passive income estimate
  RETIREMENT_CONVERSION_PCT: 0.30, // 30% of retirement accounts converted during RNOR
} as const

// State tax rates by tier
const STATE_TAX_RATES: Record<StateTaxTier, number> = {
  none: 0,        // TX, FL, WA, NV, etc.
  moderate: 0.05, // Most states
  high: 0.12      // CA, NY, NJ, etc.
}

// ==========================================
// Main Calculator
// ==========================================

/**
 * Tracks the ₹1.25L LTCG exemption usage across all equity gains.
 * The exemption is ANNUAL and applies to combined equity gains.
 * For RNOR users with multi-year window, we allow exemption × years.
 */
interface ExemptionTracker {
  totalEquityGainsINR: number
  exemptionUsedINR: number
  exemptionAvailableINR: number
}

function createExemptionTracker(rnorYears: number): ExemptionTracker {
  // For RNOR users, they can spread sales across multiple years
  // Each year gets ₹1.25L exemption, so total = ₹1.25L × years
  // For non-RNOR, assume single year (though they likely sell before move)
  const yearsForExemption = Math.max(1, rnorYears)
  return {
    totalEquityGainsINR: 0,
    exemptionUsedINR: 0,
    exemptionAvailableINR: TAX_RATES.INDIA_LTCG_EXEMPTION * yearsForExemption
  }
}

function useExemption(tracker: ExemptionTracker, gainsINR: number): { taxableGainsINR: number; exemptionUsedINR: number } {
  const remainingExemption = tracker.exemptionAvailableINR - tracker.exemptionUsedINR
  const exemptionToUse = Math.min(remainingExemption, gainsINR)
  tracker.exemptionUsedINR += exemptionToUse
  tracker.totalEquityGainsINR += gainsINR
  return {
    taxableGainsINR: Math.max(0, gainsINR - exemptionToUse),
    exemptionUsedINR: exemptionToUse
  }
}

export function calculateTaxSavingsBreakdown(input: TaxSavingsInput): TaxSavingsBreakdown {
  const items: TaxSavingsItem[] = []
  const assumptions: string[] = []

  // Create exemption tracker to ensure ₹1.25L is applied ONCE across all equity gains
  const exemptionTracker = createExemptionTracker(input.rnorYears || 0)

  if (input.residentCountry === 'USA') {
    // ==========================================
    // NON-RNOR SAVINGS (work for ALL users)
    // These are "sell before move" strategies that don't require RNOR
    // ==========================================

    // 1. Crypto Liquidation - ALWAYS show (India 30% vs US 15%)
    // Note: Crypto has SEPARATE 30% flat tax, NOT eligible for ₹1.25L exemption
    const cryptoSavings = calculateCryptoLiquidationSavings(input)
    if (cryptoSavings) items.push(cryptoSavings)

    // 2. ESPP Discount Arbitrage - Works for ALL users
    // ESPP discount is taxed as ordinary income: US ~22% vs India ~30%
    const esppSavings = calculateESPPLiquidationSavings(input, exemptionTracker)
    if (esppSavings) items.push(esppSavings)

    // 3. Retirement Account Conversion - Works for ALL users (better during RNOR)
    // Roth conversion is ordinary income: US ~22% vs India ~30%
    const retirementSavings = calculateRetirementAccountSavings(input)
    if (retirementSavings) items.push(retirementSavings)

    // 4. State Tax Avoidance - REMOVED as separate category
    // Reason: The gains are already counted in RSU/Brokerage/Crypto savings
    // State tax guidance is now included in those categories' howToCapture
    // This prevents double-counting the same gains

    // ==========================================
    // RNOR-SPECIFIC SAVINGS (require RNOR eligibility)
    // These benefit from foreign income exemption during RNOR
    // Exemption tracker ensures ₹1.25L is shared across equity gains
    // ==========================================

    // 5. RSU Liquidation (sell during RNOR) - uses shared exemption
    const rsuSavings = calculateRSULiquidationSavings(input, exemptionTracker)
    if (rsuSavings) items.push(rsuSavings)

    // 6. Brokerage Liquidation (sell during RNOR) - uses shared exemption
    const brokerageSavings = calculateBrokerageLiquidationSavings(input, exemptionTracker)
    if (brokerageSavings) items.push(brokerageSavings)

    // 7. RNOR Foreign Income (passive income)
    const rnorSavings = calculateRNORForeignIncomeSavings(input)
    if (rnorSavings) items.push(rnorSavings)

    // Document assumptions
    const effectiveSWR = input.safeWithdrawalRate
      ? input.safeWithdrawalRate / 100
      : ASSUMPTIONS.FOREIGN_INCOME_SWR

    assumptions.push(
      `India LTCG (equity): ${(TAX_RATES.INDIA_EQUITY_LTCG * 100).toFixed(1)}% above ₹1.25L exemption`,
      `US Federal LTCG: ${(TAX_RATES.US_FEDERAL_LTCG * 100).toFixed(0)}%`,
      `State tax: ${input.stateTaxTier || 'none'} (${(STATE_TAX_RATES[input.stateTaxTier || 'none'] * 100).toFixed(0)}%)`,
      `Exchange rate: ₹${input.exchangeRate.toFixed(1)}/USD`,
      `RSU gain estimate: ${(ASSUMPTIONS.RSU_GAIN_PERCENTAGE * 100).toFixed(0)}% of value`,
      `Brokerage gain estimate: ${(ASSUMPTIONS.BROKERAGE_GAIN_PERCENTAGE * 100).toFixed(0)}% of value`,
      `Foreign income SWR: ${(effectiveSWR * 100).toFixed(1)}%`
    )
  } else if (input.residentCountry === 'UAE') {
    // UAE: Only RNOR benefit (no US taxes to coordinate)
    const rnorSavings = calculateRNORForeignIncomeSavings(input)
    if (rnorSavings) items.push(rnorSavings)

    const effectiveSWR = input.safeWithdrawalRate
      ? input.safeWithdrawalRate / 100
      : ASSUMPTIONS.FOREIGN_INCOME_SWR

    assumptions.push(
      'UAE: 0% income tax, 0% capital gains tax',
      'RNOR benefit: Foreign income tax-free in India',
      `Exchange rate: ₹${input.exchangeRate.toFixed(1)}/AED`,
      `Foreign income estimate: ${(effectiveSWR * 100).toFixed(1)}% SWR`
    )
  }

  // Sort by amount (highest first)
  items.sort((a, b) => b.amountINR - a.amountINR)

  const total = items.reduce((sum, item) => sum + item.amountINR, 0)

  return {
    total,
    totalFormatted: formatINR(total),
    items,
    assumptions,
    disclaimer: 'Estimates based on current tax laws. Individual circumstances, holding periods, and DTAA credits may affect actual outcomes. Consult a CA for precise calculations.'
  }
}

// ==========================================
// Individual Savings Calculators
// ==========================================

/**
 * RSU Liquidation Savings
 *
 * Opportunity: Sell RSUs before move or during RNOR to avoid India capital gains tax.
 *
 * During RNOR: Foreign capital gains are NOT taxable in India (Section 5, IT Act)
 * After RNOR: Foreign capital gains ARE taxable at 12.5% LTCG (above ₹1.25L exemption)
 *
 * Savings = India LTCG tax avoided by selling during RNOR vs after
 *
 * NOTE: Uses shared exemption tracker - ₹1.25L exemption is shared across all equity gains
 */
function calculateRSULiquidationSavings(input: TaxSavingsInput, exemptionTracker: ExemptionTracker): TaxSavingsItem | null {
  if (!input.rnorEligible) return null
  if (!input.rsuGrants || input.rsuGrants.length === 0) return null

  // Calculate total RSU value
  const totalRSUValue = input.rsuGrants.reduce((sum, grant) => {
    return sum + (grant.vestedShares * grant.currentPricePerShare)
  }, 0)

  if (totalRSUValue <= 0) return null

  // Estimate gains (RSUs are taxed at vesting, so post-vesting appreciation is the gain)
  const estimatedGainUSD = totalRSUValue * ASSUMPTIONS.RSU_GAIN_PERCENTAGE
  const estimatedGainINR = estimatedGainUSD * input.exchangeRate

  // Use shared exemption tracker - exemption may already be partially used by other assets
  const { taxableGainsINR, exemptionUsedINR } = useExemption(exemptionTracker, estimatedGainINR)

  // Calculate India LTCG that would apply if sold AFTER RNOR
  const indiaLTCG_INR = taxableGainsINR * TAX_RATES.INDIA_EQUITY_LTCG

  // This is the tax SAVED by selling during RNOR (when India doesn't tax foreign capital gains)
  const savingsINR = Math.round(indiaLTCG_INR)

  if (savingsINR <= 10000) return null // Only show if meaningful (>₹10K)

  // Build calculation showing exemption usage
  const calculation: { label: string; value: string }[] = [
    { label: 'RSU Value', value: `$${(totalRSUValue / 1000).toFixed(0)}K` },
    { label: 'Estimated Gain (50%)', value: `$${(estimatedGainUSD / 1000).toFixed(0)}K = ${formatINR(estimatedGainINR)}` },
  ]

  if (exemptionUsedINR > 0) {
    calculation.push({ label: 'LTCG Exemption Used', value: formatINR(exemptionUsedINR) })
    calculation.push({ label: 'Taxable Gain', value: formatINR(taxableGainsINR) })
  }

  calculation.push({ label: 'India LTCG (12.5%)', value: `${formatINR(taxableGainsINR)} × 12.5% = ${formatINR(indiaLTCG_INR)}` })
  calculation.push({ label: 'Tax Saved (RNOR)', value: formatINR(savingsINR) })

  // Build howToCapture with state tax tip if applicable
  const howToCapture = [
    'Sell RSUs during RNOR window when foreign capital gains are tax-free in India',
    'Ensure shares held >1 year from vesting for US LTCG treatment (15%)',
    'US federal tax still applies - only India tax is avoided',
    'Transfer proceeds to NRE account for tax-free repatriation',
    '₹1.25L LTCG exemption is shared across all equity gains (per year)'
  ]

  // Add state tax optimization tip for high-tax state residents
  if (input.stateTaxTier === 'high') {
    howToCapture.push('💡 Tip: Move to no-tax state (TX, FL) before selling to also avoid 10-13% state tax')
  }

  return {
    id: 'rsu-liquidation',
    category: 'RSU Capital Gains',
    description: `Sell $${(totalRSUValue / 1000).toFixed(0)}K RSUs during RNOR window`,
    amountINR: savingsINR,
    amountFormatted: formatINR(savingsINR),
    timing: 'during_rnor',
    timingDescription: 'During RNOR (foreign capital gains exempt)',
    howToCapture,
    confidence: 'medium',
    requiresCAReview: totalRSUValue > 100000,
    calculation
  }
}

/**
 * Brokerage Account Liquidation Savings
 *
 * Same principle as RSU - sell during RNOR to avoid India LTCG.
 *
 * NOTE: Uses shared exemption tracker - ₹1.25L exemption is shared across all equity gains
 */
function calculateBrokerageLiquidationSavings(input: TaxSavingsInput, exemptionTracker: ExemptionTracker): TaxSavingsItem | null {
  if (!input.rnorEligible) return null
  if (!input.taxableBrokerage || input.taxableBrokerage <= 0) return null

  const brokerageValue = input.taxableBrokerage

  // Calculate gains
  let estimatedGainUSD: number
  let gainSource: string
  if (input.brokerageCostBasis && input.brokerageCostBasis > 0) {
    // User provided cost basis
    estimatedGainUSD = Math.max(0, brokerageValue - input.brokerageCostBasis)
    gainSource = 'from cost basis'
  } else {
    // Assume 30% is gains (70% cost basis)
    estimatedGainUSD = brokerageValue * ASSUMPTIONS.BROKERAGE_GAIN_PERCENTAGE
    gainSource = 'estimated 30%'
  }

  if (estimatedGainUSD <= 0) return null

  const estimatedGainINR = estimatedGainUSD * input.exchangeRate

  // Use shared exemption tracker - exemption may already be partially used by RSUs
  const { taxableGainsINR, exemptionUsedINR } = useExemption(exemptionTracker, estimatedGainINR)

  // India LTCG that would apply after RNOR
  const indiaLTCG_INR = taxableGainsINR * TAX_RATES.INDIA_EQUITY_LTCG

  const savingsINR = Math.round(indiaLTCG_INR)

  if (savingsINR <= 10000) return null

  // Build calculation showing exemption usage
  const calculation: { label: string; value: string }[] = [
    { label: 'Brokerage Value', value: `$${(brokerageValue / 1000).toFixed(0)}K` },
    { label: `Gain (${gainSource})`, value: `$${(estimatedGainUSD / 1000).toFixed(0)}K = ${formatINR(estimatedGainINR)}` },
  ]

  if (exemptionUsedINR > 0) {
    calculation.push({ label: 'LTCG Exemption Used', value: formatINR(exemptionUsedINR) })
  } else {
    calculation.push({ label: 'LTCG Exemption', value: '₹0 (already used by other assets)' })
  }

  calculation.push({ label: 'Taxable Gain', value: formatINR(taxableGainsINR) })
  calculation.push({ label: 'India LTCG (12.5%)', value: `${formatINR(taxableGainsINR)} × 12.5% = ${formatINR(indiaLTCG_INR)}` })
  calculation.push({ label: 'Tax Saved (RNOR)', value: formatINR(savingsINR) })

  // Build howToCapture with state tax tip if applicable
  const howToCapture = [
    'Harvest long-term capital gains during RNOR window',
    'US federal LTCG (15%) still applies - India tax avoided',
    'Consider tax-loss harvesting to offset US gains',
    'Reinvest in India-domiciled funds post-RNOR',
    '₹1.25L LTCG exemption is shared across all equity gains (per year)'
  ]

  // Add state tax optimization tip for high-tax state residents
  if (input.stateTaxTier === 'high') {
    howToCapture.push('💡 Tip: Move to no-tax state (TX, FL) before selling to also avoid 10-13% state tax')
  }

  return {
    id: 'brokerage-liquidation',
    category: 'Brokerage Capital Gains',
    description: `Sell $${(brokerageValue / 1000).toFixed(0)}K brokerage holdings during RNOR`,
    amountINR: savingsINR,
    amountFormatted: formatINR(savingsINR),
    timing: 'during_rnor',
    timingDescription: 'During RNOR (foreign capital gains exempt)',
    howToCapture,
    confidence: input.brokerageCostBasis ? 'high' : 'medium',
    requiresCAReview: brokerageValue > 100000,
    calculation
  }
}

/**
 * ESPP Liquidation Savings
 *
 * Works for ALL users (RNOR and non-RNOR).
 *
 * ESPP has unique tax treatment:
 * - Discount portion (typically 15%) is taxed as ordinary income
 * - Additional appreciation is taxed as capital gains (uses shared ₹1.25L exemption)
 *
 * For RNOR users: Full India tax avoided (both discount + appreciation)
 * For non-RNOR users: Ordinary income arbitrage on discount (US 22% vs India 30%)
 *
 * NOTE: For RNOR, appreciation portion uses shared exemption tracker
 */
function calculateESPPLiquidationSavings(input: TaxSavingsInput, exemptionTracker: ExemptionTracker): TaxSavingsItem | null {
  if (!input.esppBalance || input.esppBalance <= 0) return null

  const esppValue = input.esppBalance

  // ESPP breakdown:
  // 1. Discount portion (15% of purchase price) - taxed as ordinary income
  // 2. Appreciation above purchase price - taxed as capital gains
  const purchasePrice = esppValue / (1 + ASSUMPTIONS.ESPP_DISCOUNT_RATE + ASSUMPTIONS.ESPP_GAIN_PERCENTAGE)
  const discountAmount = purchasePrice * ASSUMPTIONS.ESPP_DISCOUNT_RATE
  const appreciationAmount = esppValue - purchasePrice - discountAmount

  const discountINR = discountAmount * input.exchangeRate
  const appreciationINR = appreciationAmount * input.exchangeRate

  let savingsINR: number
  let description: string
  let timing: 'before_move' | 'during_rnor'
  let timingDescription: string
  let howToCapture: string[]
  let calculation: { label: string; value: string }[]

  if (input.rnorEligible) {
    // RNOR: Full India tax avoided (both discount + appreciation)
    const indiaTaxOnDiscount = discountINR * TAX_RATES.INDIA_INCOME_TAX_AVG

    // IMPORTANT: Calculate potential savings BEFORE using exemption
    // This prevents "wasting" exemption if savings are below threshold
    const potentialAppreciationTax = Math.max(0, appreciationINR - (exemptionTracker.exemptionAvailableINR - exemptionTracker.exemptionUsedINR)) * TAX_RATES.INDIA_EQUITY_LTCG
    const potentialSavings = indiaTaxOnDiscount + potentialAppreciationTax

    // Early return if savings too small - don't consume exemption
    if (Math.round(potentialSavings) <= 10000) return null

    // Now safe to use exemption (we know savings are meaningful)
    const { taxableGainsINR, exemptionUsedINR } = useExemption(exemptionTracker, appreciationINR)
    const indiaTaxOnAppreciation = taxableGainsINR * TAX_RATES.INDIA_EQUITY_LTCG

    savingsINR = Math.round(indiaTaxOnDiscount + indiaTaxOnAppreciation)

    description = `Sell $${(esppValue / 1000).toFixed(0)}K ESPP shares during RNOR`
    timing = 'during_rnor'
    timingDescription = 'During RNOR (discount + gains exempt in India)'
    howToCapture = [
      'Sell ESPP shares during RNOR to avoid India taxation',
      'Discount (ordinary income) + appreciation (LTCG) both exempt during RNOR',
      'Hold >2 years from grant + 1 year from purchase for US qualifying disposition',
      '₹1.25L LTCG exemption shared across all equity gains'
    ]
    calculation = [
      { label: 'ESPP Value', value: `$${(esppValue / 1000).toFixed(0)}K` },
      { label: 'Discount (~15%)', value: `$${(discountAmount / 1000).toFixed(1)}K = ${formatINR(discountINR)}` },
      { label: 'India Tax on Discount (30%)', value: formatINR(indiaTaxOnDiscount) },
      { label: 'Appreciation', value: `$${(appreciationAmount / 1000).toFixed(1)}K = ${formatINR(appreciationINR)}` },
    ]

    if (exemptionUsedINR > 0) {
      calculation.push({ label: 'LTCG Exemption Used', value: formatINR(exemptionUsedINR) })
    }

    calculation.push({ label: 'India Tax on Appreciation (12.5%)', value: formatINR(indiaTaxOnAppreciation) })
    calculation.push({ label: 'Total Tax Saved (RNOR)', value: formatINR(savingsINR) })
  } else {
    // Non-RNOR: No clear tax arbitrage exists
    // ESPP discount is taxed in US at sale as ordinary income
    // Under DTAA, India gives credit for US taxes paid - no double taxation
    // The "arbitrage" we previously showed was conceptually incorrect
    //
    // However, there IS a timing benefit: Sell before becoming India resident
    // to avoid complexity of cross-border tax reporting on ESPP
    //
    // We return null for non-RNOR users since there's no quantifiable savings
    return null
  }

  // Note: Threshold check moved earlier (before using exemption) for RNOR users
  // For non-RNOR, we return null above, so savingsINR is always set here

  return {
    id: 'espp-liquidation',
    category: 'ESPP Sale Timing',
    description,
    amountINR: savingsINR,
    amountFormatted: formatINR(savingsINR),
    timing,
    timingDescription,
    howToCapture,
    confidence: 'medium',
    requiresCAReview: true, // ESPP always complex
    calculation
  }
}

/**
 * Cryptocurrency Liquidation Savings
 *
 * KEY INSIGHT: This works for ALL users, NOT just RNOR eligible!
 *
 * India crypto tax (Section 115BBH): Flat 30% on gains, NO exemptions, NO deductions
 * US crypto tax: 15% federal LTCG + state tax (0-13% depending on state)
 *
 * Strategy: Sell crypto in US BEFORE becoming India tax resident
 * Savings = India 30% - US (federal + state)
 *
 * IMPORTANT: For high-tax states (CA, NY), the arbitrage is much smaller!
 * - No-tax state (TX, FL): 30% - 15% = 15% savings
 * - High-tax state (CA): 30% - 28% = 2% savings
 */
function calculateCryptoLiquidationSavings(input: TaxSavingsInput): TaxSavingsItem | null {
  // NOTE: Does NOT require RNOR - this is a "sell before move" strategy
  if (!input.cryptoBalance || input.cryptoBalance <= 0) return null

  const cryptoValue = input.cryptoBalance

  // Estimate gains (crypto is volatile, assume 50% is gains)
  const estimatedGainUSD = cryptoValue * ASSUMPTIONS.CRYPTO_GAIN_PERCENTAGE
  const estimatedGainINR = estimatedGainUSD * input.exchangeRate

  if (estimatedGainUSD <= 0) return null

  // Calculate tax difference
  // India: 30% flat (no exemptions for crypto)
  const indiaTaxINR = estimatedGainINR * TAX_RATES.INDIA_CRYPTO_TAX

  // US: Federal LTCG (15%) + State tax (varies by state)
  const stateTaxRate = STATE_TAX_RATES[input.stateTaxTier || 'none']
  const usFederalTaxINR = estimatedGainINR * TAX_RATES.US_FEDERAL_LTCG
  const usStateTaxINR = estimatedGainINR * stateTaxRate
  const usTotalTaxINR = usFederalTaxINR + usStateTaxINR
  const effectiveUSRate = TAX_RATES.US_FEDERAL_LTCG + stateTaxRate

  // Savings = India tax - US total tax (federal + state)
  const savingsINR = Math.round(indiaTaxINR - usTotalTaxINR)

  // Only show if meaningful (>₹10K savings)
  if (savingsINR <= 10000) return null

  // Determine timing based on RNOR eligibility
  const timing: 'before_move' | 'during_rnor' = input.rnorEligible ? 'during_rnor' : 'before_move'
  const timingDescription = input.rnorEligible
    ? 'Before move or during RNOR (foreign gains not taxed in India)'
    : 'Sell before becoming India tax resident'

  // Build calculation breakdown showing full logic
  const calculationSteps: { label: string; value: string }[] = [
    { label: 'Crypto Value', value: `$${(cryptoValue / 1000).toFixed(0)}K` },
    { label: 'Estimated Gain (50%)', value: `$${(estimatedGainUSD / 1000).toFixed(0)}K = ${formatINR(estimatedGainINR)}` },
    { label: 'India Tax (30% flat)', value: formatINR(indiaTaxINR) },
    { label: 'US Federal Tax (15%)', value: formatINR(usFederalTaxINR) },
  ]

  // Add state tax line if applicable
  if (stateTaxRate > 0) {
    calculationSteps.push({
      label: `US State Tax (${(stateTaxRate * 100).toFixed(0)}%)`,
      value: formatINR(usStateTaxINR)
    })
    calculationSteps.push({
      label: `US Total (${(effectiveUSRate * 100).toFixed(0)}%)`,
      value: formatINR(usTotalTaxINR)
    })
  }

  calculationSteps.push({
    label: 'Net Savings',
    value: `${formatINR(indiaTaxINR)} - ${formatINR(usTotalTaxINR)} = ${formatINR(savingsINR)}`
  })

  // Adjust howToCapture based on state tax situation
  const howToCapture = [
    'Sell cryptocurrency while still US tax resident',
    `US taxes crypto at ${(effectiveUSRate * 100).toFixed(0)}% (15% federal${stateTaxRate > 0 ? ` + ${(stateTaxRate * 100).toFixed(0)}% state` : ''})`,
    'India taxes crypto at flat 30% with NO exemptions or loss offsets',
    'Complete sale before becoming India tax resident',
    'Keep records of purchase date and cost basis for US filing'
  ]

  // Add warning for high-tax state residents
  if (input.stateTaxTier === 'high') {
    howToCapture.push('⚠️ High state tax reduces arbitrage - consider moving to no-tax state first')
  }

  return {
    id: 'crypto-liquidation',
    category: 'Crypto Tax Arbitrage',
    description: `Sell $${(cryptoValue / 1000).toFixed(0)}K crypto in US (${(effectiveUSRate * 100).toFixed(0)}% vs India 30%)`,
    amountINR: savingsINR,
    amountFormatted: formatINR(savingsINR),
    timing,
    timingDescription,
    howToCapture,
    confidence: stateTaxRate > 0 ? 'medium' : 'high', // Lower confidence for high-tax states
    requiresCAReview: cryptoValue > 50000,
    calculation: calculationSteps
  }
}

/**
 * RNOR Foreign Income Savings
 *
 * During RNOR: Foreign passive income (dividends, interest) is NOT taxed in India
 * After RNOR: Taxed at slab rates (~30%)
 *
 * IMPORTANT: Only dividend-generating assets produce passive income:
 * - Taxable brokerage: YES (dividends ~2% yield)
 * - 401k/IRA: NO (must withdraw to access, which is separate strategy)
 * - Crypto: NO (no passive income)
 * - RSUs: MAYBE (depends on if company pays dividends)
 *
 * Note: This is for PASSIVE income. Capital gains are handled separately above.
 */
function calculateRNORForeignIncomeSavings(input: TaxSavingsInput): TaxSavingsItem | null {
  if (!input.rnorEligible || input.rnorYears <= 0) return null

  // Only count assets that generate PASSIVE income (dividends, interest)
  // Exclude: 401k/IRA (requires withdrawal), Crypto (no dividends)
  const dividendYieldingAssets = (input.taxableBrokerage || 0) + (input.esppBalance || 0)

  // RSUs only if significant (assume company pays ~1% dividend)
  const rsuValue = (input.rsuGrants || []).reduce(
    (sum, g) => sum + g.vestedShares * g.currentPricePerShare,
    0
  )

  // Total passive-income-generating assets
  const passiveIncomeBase = dividendYieldingAssets + rsuValue

  if (passiveIncomeBase <= 0) return null

  // Dividend yield is typically 1.5-2% for US stocks (not SWR which is for withdrawals)
  const DIVIDEND_YIELD = 0.018 // 1.8% average dividend yield

  // Estimate annual passive foreign income (dividends + interest)
  const estimatedAnnualIncomeUSD = passiveIncomeBase * DIVIDEND_YIELD
  const estimatedAnnualIncomeINR = estimatedAnnualIncomeUSD * input.exchangeRate

  // India tax that would apply after RNOR (30% slab rate)
  const annualTaxSavedINR = estimatedAnnualIncomeINR * TAX_RATES.INDIA_INCOME_TAX_AVG

  // Total over RNOR period
  const totalSavingsINR = Math.round(annualTaxSavedINR * input.rnorYears)

  if (totalSavingsINR <= 10000) return null

  return {
    id: 'rnor-foreign-income',
    category: 'RNOR Passive Income',
    description: `Tax-free foreign dividends for ${input.rnorYears} year${input.rnorYears > 1 ? 's' : ''}`,
    amountINR: totalSavingsINR,
    amountFormatted: formatINR(totalSavingsINR),
    timing: 'during_rnor',
    timingDescription: `During ${input.rnorYears}-year RNOR window`,
    howToCapture: [
      'Keep dividend-paying investments during RNOR window',
      'Foreign dividends are tax-free in India during RNOR',
      'File ITR declaring RNOR status with Form 67 for foreign income',
      'Note: 401k/IRA withdrawals are separate (not passive income)'
    ],
    confidence: 'estimated',
    requiresCAReview: true,
    calculation: [
      { label: 'Dividend-generating Assets', value: `$${(passiveIncomeBase / 1000).toFixed(0)}K` },
      { label: 'Breakdown', value: `Brokerage $${((input.taxableBrokerage || 0) / 1000).toFixed(0)}K + RSU $${(rsuValue / 1000).toFixed(0)}K` },
      { label: `Annual Dividends (~1.8% yield)`, value: `$${(estimatedAnnualIncomeUSD / 1000).toFixed(1)}K/yr = ${formatINR(estimatedAnnualIncomeINR)}/yr` },
      { label: 'India Tax Avoided (30%)', value: `${formatINR(annualTaxSavedINR)}/yr` },
      { label: `Total (${input.rnorYears} yr${input.rnorYears > 1 ? 's' : ''})`, value: formatINR(totalSavingsINR) },
      { label: 'Note', value: '401k/Crypto excluded (no passive income)' },
    ]
  }
}

/**
 * 401(k)/IRA Roth Conversion Savings
 *
 * Works for ALL users (RNOR and non-RNOR).
 *
 * Roth conversion = ordinary income. The arbitrage:
 * - US tax: ~22% ordinary income
 * - India tax: ~30% slab rate
 *
 * For RNOR users: Full India tax avoided (conversion exempt during RNOR)
 * For non-RNOR users: Ordinary income arbitrage (US 22% vs India 30% = 8% savings)
 */
function calculateRetirementAccountSavings(input: TaxSavingsInput): TaxSavingsItem | null {
  const totalRetirement = (input.balance401k || 0) + (input.balanceTraditionalIRA || 0)
  if (totalRetirement <= 0) return null

  // Recommend converting 30% (can spread over time for bracket management)
  const conversionAmountUSD = totalRetirement * ASSUMPTIONS.RETIREMENT_CONVERSION_PCT
  const conversionAmountINR = conversionAmountUSD * input.exchangeRate

  let savingsINR: number
  let description: string
  let timing: 'before_move' | 'during_rnor'
  let timingDescription: string
  let howToCapture: string[]
  let calculation: { label: string; value: string }[]

  if (input.rnorEligible && input.rnorYears > 0) {
    // RNOR: Full India tax avoided (conversion exempt during RNOR)
    const indiaTaxAvoided = conversionAmountINR * TAX_RATES.INDIA_INCOME_TAX_AVG
    savingsINR = Math.round(indiaTaxAvoided)

    description = `Convert $${(conversionAmountUSD / 1000).toFixed(0)}K to Roth during RNOR`
    timing = 'during_rnor'
    timingDescription = 'During RNOR (conversion income exempt in India)'
    howToCapture = [
      'Convert Traditional 401k/IRA to Roth IRA during RNOR years',
      'US tax (~22%) applies on conversion - plan for this',
      'India doesn\'t tax during RNOR (foreign income exempt)',
      'Spread conversions across RNOR years for bracket management',
      'Roth grows tax-free and withdrawals are tax-free'
    ]
    calculation = [
      { label: 'Retirement Balance', value: `$${(totalRetirement / 1000).toFixed(0)}K` },
      { label: 'Suggested Conversion (30%)', value: `$${(conversionAmountUSD / 1000).toFixed(0)}K` },
      { label: 'India Tax Avoided (30%)', value: formatINR(savingsINR) },
      { label: 'Note', value: 'US tax (~22%) still applies' },
    ]
  } else {
    // Non-RNOR: Timing arbitrage due to DTAA mechanics
    //
    // If converted while US resident: Pay US tax only (~22%)
    // If converted while India resident:
    //   - US still taxes at source (~22%)
    //   - India also wants to tax (~30%)
    //   - DTAA gives credit: Pay 22% to US + 8% to India = 30% total
    //
    // Savings = 30% - 22% = 8% by converting BEFORE becoming India resident
    const usTax = conversionAmountINR * TAX_RATES.US_ORDINARY_INCOME_AVG
    const indiaTax = conversionAmountINR * TAX_RATES.INDIA_INCOME_TAX_AVG
    const dtaaCredit = usTax // India gives credit for US tax paid
    const additionalIndiaTax = Math.max(0, indiaTax - dtaaCredit)
    savingsINR = Math.round(additionalIndiaTax)

    description = `Convert $${(conversionAmountUSD / 1000).toFixed(0)}K to Roth before move`
    timing = 'before_move'
    timingDescription = 'Convert while US tax resident (avoid India additional tax)'
    howToCapture = [
      'Convert Traditional 401k/IRA to Roth before becoming India resident',
      'If converted while India resident: US (22%) + India (8% after DTAA credit) = 30%',
      'If converted before move: US (22%) only - save the 8% India differential',
      'Spread over multiple years to manage US tax brackets',
      'Roth grows tax-free and withdrawals are tax-free in US'
    ]
    calculation = [
      { label: 'Retirement Balance', value: `$${(totalRetirement / 1000).toFixed(0)}K` },
      { label: 'Suggested Conversion (30%)', value: `$${(conversionAmountUSD / 1000).toFixed(0)}K = ${formatINR(conversionAmountINR)}` },
      { label: 'If converted while India resident:', value: '' },
      { label: '  US Tax (withheld)', value: formatINR(usTax) },
      { label: '  India Tax (30%)', value: formatINR(indiaTax) },
      { label: '  DTAA Credit', value: `-${formatINR(dtaaCredit)}` },
      { label: '  Additional to India', value: formatINR(additionalIndiaTax) },
      { label: 'If converted before move:', value: `US Tax only = ${formatINR(usTax)}` },
      { label: 'Savings', value: formatINR(savingsINR) },
    ]
  }

  // Only show if meaningful
  if (savingsINR <= 50000) return null // Higher threshold for retirement (>₹50K)

  return {
    id: 'retirement-roth-conversion',
    category: '401(k) Roth Conversion',
    description,
    amountINR: savingsINR,
    amountFormatted: formatINR(savingsINR),
    timing,
    timingDescription,
    howToCapture,
    confidence: 'estimated',
    requiresCAReview: true,
    calculation
  }
}

// NOTE: State Tax Avoidance was removed as a separate category
// to prevent double-counting. State tax optimization tips are now
// included in the RSU/Brokerage howToCapture sections for high-tax state users.

// ==========================================
// Helpers
// ==========================================

function formatINR(amount: number): string {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(1)}Cr`
  }
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)}L`
  }
  return `₹${amount.toLocaleString('en-IN')}`
}

/**
 * Get state tax rate from tier
 */
export function getStateTaxRate(tier: StateTaxTier | undefined): number {
  return STATE_TAX_RATES[tier || 'none']
}

/**
 * Get descriptive text for state tax tier
 */
export function getStateTaxTierDescription(tier: StateTaxTier | undefined): string {
  switch (tier) {
    case 'none':
      return 'No state income tax (TX, FL, WA, NV, TN, WY, SD, AK, NH)'
    case 'high':
      return 'High state tax (CA, NY, NJ, OR, MN)'
    case 'moderate':
    default:
      return 'Moderate state tax (most other states)'
  }
}
